import { type ReactNode, useMemo } from 'react';
import type { Key } from 'react-aria-components';
import { msg } from '@gonfalon/messages/msg';
import { Collection, Header, ListBox, ListBoxSection } from '@launchpad-ui/components';

import type { Command } from '../types/command';
import type { RankedSection } from '../types/command-strategy';

import { CommandPaletteListBoxItem } from './command-palette-list-box-item';

import styles from './command-palette-autocomplete.module.css';

interface SectionRow {
  /** Stable id for RAC's Collection -- section.id, or 'ungrouped' for the synthetic bucket. */
  id: string;
  section: RankedSection['section'];
  children: Command[];
}

export interface CommandPaletteResultsListProps {
  /** Already-ranked sections (the strategy ran upstream). */
  sections: RankedSection[];
  /** Called when the user activates a row (Enter, click). */
  onAction: (id: Key) => void;
  /**
   * JSX rendered when the listbox is empty (no sections, or all
   * sections empty). Pre-computed by the caller as a `ReactNode`
   * rather than a render function; the listbox passes it through
   * RAC's `renderEmptyState` callback. The caller decides which
   * empty-state variant to mount (generic "No matches",
   * source-declared root emptyState, snap-prefix discovery hint).
   */
  emptyState: ReactNode;
}

/**
 * Pure listbox + section presenter. Takes ranked sections (already
 * in display order, already filtered) and renders a RAC ListBox
 * with one `<ListBoxSection>` per section, one `<ListBoxItem>` per
 * command.
 *
 * No filtering, no ranking, no strategy invocation -- those run
 * upstream in `CommandPaletteResults`, which decides the candidate
 * set and runs the ranking strategy. This component is the shared
 * rendering surface.
 *
 * Empty state is delegated to the caller via the `emptyState` prop
 * because the right empty copy depends on context: entered roots
 * show their own declared content, snap-prefix mode shows a
 * discovery hint, generic empty falls back to "No matches."
 */
export function CommandPaletteResultsList({ sections, onAction, emptyState }: CommandPaletteResultsListProps) {
  // Materialise the section rows once per `sections` change. We
  // construct SectionRow rather than passing RankedSection directly
  // because RAC's <Collection items=...> wants an `id` field and
  // RankedSection.section can be null (ungrouped synthetic bucket).
  const rows = useMemo<SectionRow[]>(() => {
    const out: SectionRow[] = [];
    for (const ranked of sections) {
      if (ranked.commands.length === 0) {
        continue;
      }
      out.push({
        id: ranked.section?.id ?? 'ungrouped',
        section: ranked.section,
        children: ranked.commands,
      });
    }
    return out;
  }, [sections]);

  return (
    <ListBox
      shouldFocusOnHover
      className={styles.listbox}
      aria-label={msg('commandPalette.results.label')}
      renderEmptyState={() => emptyState}
      items={rows}
      onAction={onAction}
    >
      {(row) => (
        <ListBoxSection id={row.id}>
          {row.section !== null && <Header>{row.section.label}</Header>}
          <Collection items={row.children}>{(command) => <CommandPaletteListBoxItem command={command} />}</Collection>
        </ListBoxSection>
      )}
    </ListBox>
  );
}
